import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/d7cdb151-3b6d-4b6f-9ff2-fbeb6ae92f5e/images/a80c5390203ed6a344ed1eb7e57ae9bd55cbc63c41299f85ec09ca8fa7817c49.jpeg";
const HERO_BADGE = "https://customer-assets-jt897jd0.emergentagent.net/job_compound-glow/artifacts/pxu6u8lx_Holographic%20Tatau%20Typography%20Edit%20%28A3%29.png";

const lines = ["RESEARCH", "GRADE", "PEPTIDES"];

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({ y: 0, transition: { duration: 0.9, delay: 0.25 + i * 0.12, ease: [0.16, 1, 0.3, 1] } }),
};

export default function Hero({ onExplore }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section id="top" ref={ref} className="relative min-h-[100vh] overflow-hidden flex items-center pt-20">
      <div className="absolute inset-0 grid-radial opacity-60" />
      <div className="absolute -left-40 top-1/3 w-[520px] h-[520px] rounded-full bg-[#00F0FF]/10 blur-[130px]" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 w-full grid lg:grid-cols-2 gap-8 items-center">
        <motion.div style={{ y: textY }} className="z-10">
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}
            className="text-[11px] tracking-[0.4em] uppercase text-[#00F0FF] mb-6 font-mono2"
            data-testid="hero-eyebrow"
          >
            [ POLYONPEPS.COM ] · Lab-Verified · &gt;99% Purity
          </motion.p>

          <h1 className="font-display font-black tracking-tighter leading-[0.85] text-5xl sm:text-6xl lg:text-7xl uppercase" data-testid="hero-title">
            {lines.map((l, i) => (
              <span key={l} className="block overflow-hidden">
                <motion.span
                  custom={i} variants={lineVariant} initial="hidden" animate="show"
                  className={`block ${i === 2 ? "text-[#00F0FF] neon-text" : "text-white"}`}
                >
                  {l}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-8 max-w-md text-sm md:text-base text-slate-400 font-mono2 leading-relaxed"
          >
            Single-vial clarity. Precision compounds &amp; stacks with best-use guidance and easy dosing — add to cart in one tap.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.7 }}
            className="mt-10 flex items-center gap-4"
          >
            <button
              onClick={onExplore}
              className="group flex items-center gap-3 bg-[#00F0FF] text-black px-7 py-3.5 text-xs uppercase tracking-widest font-bold font-mono2 hover:shadow-[0_0_30px_rgba(0,240,255,0.6)] transition-shadow"
              data-testid="hero-explore-btn"
            >
              Explore Compounds
              <ArrowDown size={15} className="group-hover:translate-y-1 transition-transform" />
            </button>
          </motion.div>
        </motion.div>

        <motion.div style={{ y: imgY, scale: imgScale }} className="relative flex justify-center lg:justify-end">
          <motion.img
            src={HERO_IMG}
            alt="Glowing peptide vial"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1, y: [0, -18, 0] }}
            transition={{ opacity: { delay: 0.4, duration: 1 }, y: { repeat: Infinity, duration: 6, ease: "easeInOut" } }}
            className="w-full max-w-xl rounded-lg vial-glow object-cover"
            data-testid="hero-image"
          />
          <img
            src={HERO_BADGE}
            alt="polyonpeps.com — thank you for your order"
            className="absolute bottom-2 right-2 w-[30%] pointer-events-none select-none mix-blend-screen opacity-90 drop-shadow-[0_0_14px_rgba(0,240,255,0.45)]"
            data-testid="hero-badge"
          />
        </motion.div>
      </div>
    </section>
  );
}
